import { generateBrandColors } from "../../config/colors";
import type {
  BrandSummary,
  CampaignReportResponse,
  CampaignSummary,
  DailyMetrics,
  UnitsByBrandItem,
} from "../../types";
import {
  formatReportDateLabel,
  sortByReportDate,
} from "../../utils/dateFormatting";

export type OriginalMetricTrend = "up" | "down" | "neutral";
export type OriginalRoasTone = "excellent" | "good" | "needs_improvement";

export interface OriginalMetricCardViewModel {
  title: string;
  value: string;
  subtitle?: string;
  trend: OriginalMetricTrend;
}

export interface OriginalBrandCardViewModel extends BrandSummary {
  roasValue: number;
  ctrPercentage: number;
  totalSalesDisplay: string;
  totalSpendDisplay: string;
  totalImpressionsDisplay: string;
  roasTone: OriginalRoasTone;
}

export interface OriginalCampaignRowViewModel extends CampaignSummary {
  roasValue: number;
  totalSalesDisplay: string;
  totalSpendDisplay: string;
  roasTone: OriginalRoasTone;
}

export interface OriginalBarChartDataset {
  label: string;
  data: number[];
  backgroundColor: string | string[];
}

export interface OriginalLayoutViewModel {
  reportBadge: string;
  dateRangeLabel: string;
  recordsProcessedLabel: string;
  daysLabel: string;
  overallMetrics: {
    totalSales: OriginalMetricCardViewModel;
    avgDailySales: OriginalMetricCardViewModel;
    totalSpend: OriginalMetricCardViewModel;
    roas: OriginalMetricCardViewModel;
    impressions: OriginalMetricCardViewModel;
    totalClicks: OriginalMetricCardViewModel;
    clickRate: OriginalMetricCardViewModel;
    ntbSalesShare: OriginalMetricCardViewModel;
  };
  brandCards: OriginalBrandCardViewModel[];
  brandSalesData: UnitsByBrandItem[];
  salesDistributionSubtitle: string;
  roasChart: {
    labels: string[];
    values: number[];
    colors: string[];
  };
  roasTargetSubtitle: string;
  dailyTrendChart: {
    labels: string[];
    datasets: OriginalBarChartDataset[];
  };
  dailyTrendSubtitle: string;
  campaignDetailsSubtitle: string;
  campaignRows: OriginalCampaignRowViewModel[];
}

const ROAS_THRESHOLDS = {
  EXCELLENT: 3,
  GOOD: 2,
  TARGET: 1,
} as const;

const CTR_THRESHOLDS = {
  ABOVE_AVERAGE: 1,
  AVERAGE: 0.3,
} as const;

const SALES_FORMAT_THRESHOLDS = {
  MILLION: 1000000,
  THOUSAND: 10000,
  THOUSAND_DIVISOR: 1000,
} as const;

const IMPRESSIONS_FORMAT_THRESHOLD = 1000000;

const parseNumber = (value: number | string | null | undefined): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = parseFloat(value.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const parseOptionalNumber = (
  value: number | string | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = parseNumber(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatSales = (value: number | string): string => {
  const numeric = parseNumber(value);
  if (numeric >= SALES_FORMAT_THRESHOLDS.MILLION) {
    return `$${Math.ceil(numeric / SALES_FORMAT_THRESHOLDS.MILLION)}M`;
  }
  if (numeric >= SALES_FORMAT_THRESHOLDS.THOUSAND) {
    return `$${Math.ceil(numeric / SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR)}k`;
  }
  return `$${Math.ceil(numeric).toLocaleString()}`;
};

const formatMoney = (
  value: number | string,
  minimumFractionDigits = 2,
  maximumFractionDigits?: number,
): string =>
  parseNumber(value).toLocaleString("en-US", {
    minimumFractionDigits,
    ...(maximumFractionDigits !== undefined ? { maximumFractionDigits } : {}),
  });

const formatSummaryImpressions = (value: number): string =>
  value >= IMPRESSIONS_FORMAT_THRESHOLD
    ? `${(value / IMPRESSIONS_FORMAT_THRESHOLD).toFixed(1)}M`
    : `${(value / SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR).toFixed(1)}k`;

const formatBrandImpressions = (value: number): string => {
  if (value >= IMPRESSIONS_FORMAT_THRESHOLD) {
    return `${(value / IMPRESSIONS_FORMAT_THRESHOLD).toFixed(1)}M`;
  }
  if (value >= SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR) {
    return `${(value / SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR).toFixed(1)}k`;
  }
  return value.toLocaleString();
};

const dailyAverage = (value: number, totalDays: number): number =>
  totalDays > 0 ? value / totalDays : 0;

const dailyAverageLabel = (value: number, totalDays: number): string =>
  Math.round(dailyAverage(value, totalDays)).toLocaleString();

const roasToneFor = (roas: number): OriginalRoasTone => {
  if (roas >= ROAS_THRESHOLDS.EXCELLENT) {
    return "excellent";
  }
  if (roas >= ROAS_THRESHOLDS.GOOD) {
    return "good";
  }
  return "needs_improvement";
};

const trendForRoas = (roas: number): OriginalMetricTrend => {
  if (roas >= ROAS_THRESHOLDS.EXCELLENT) {
    return "up";
  }
  if (roas >= ROAS_THRESHOLDS.GOOD) {
    return "neutral";
  }
  return "down";
};

const buildBrandCards = (
  brands: BrandSummary[],
): OriginalBrandCardViewModel[] =>
  brands.map((brand) => {
    const roasValue = parseNumber(brand.roas);
    const ctrPercentage =
      brand.total_impressions > 0
        ? (brand.total_clicks / brand.total_impressions) * 100
        : 0;

    return {
      ...brand,
      roasValue,
      ctrPercentage,
      totalSalesDisplay: formatMoney(brand.total_sales),
      totalSpendDisplay: formatMoney(brand.total_spend),
      totalImpressionsDisplay: formatBrandImpressions(brand.total_impressions),
      roasTone: roasToneFor(roasValue),
    };
  });

const buildBrandSalesData = (brands: BrandSummary[]): UnitsByBrandItem[] => {
  const totalSales = brands.reduce(
    (sum, brand) => sum + parseNumber(brand.total_sales),
    0,
  );

  return brands.map((brand) => {
    const sales = parseNumber(brand.total_sales);
    return {
      brand_name: brand.brand,
      units: sales,
      percentage: totalSales > 0 ? (sales / totalSales) * 100 : 0,
    };
  });
};

const buildDailyTrendChart = (
  dailyMetrics: DailyMetrics[],
): OriginalLayoutViewModel["dailyTrendChart"] => {
  const sortedDailyMetrics = sortByReportDate(
    dailyMetrics,
    (metric) => metric.report_date,
  );

  return {
    labels: sortedDailyMetrics.map((metric) =>
      formatReportDateLabel(metric.report_date),
    ),
    datasets: [
      {
        label: "Sales ($)",
        data: sortedDailyMetrics.map((metric) => parseNumber(metric.total_sales)),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
      },
      {
        label: "Spend ($)",
        data: sortedDailyMetrics.map((metric) => parseNumber(metric.total_spend)),
        backgroundColor: "rgba(239, 68, 68, 0.8)",
      },
    ],
  };
};

const buildCampaignRows = (
  campaigns: CampaignSummary[],
): OriginalCampaignRowViewModel[] =>
  [...campaigns]
    .sort((a, b) => parseNumber(b.total_sales) - parseNumber(a.total_sales))
    .map((campaign) => {
      const roasValue = parseNumber(campaign.roas);
      return {
        ...campaign,
        roasValue,
        totalSalesDisplay: formatMoney(campaign.total_sales),
        totalSpendDisplay: formatMoney(campaign.total_spend),
        roasTone: roasToneFor(roasValue),
      };
    });

export const buildOriginalLayoutViewModel = (
  data: CampaignReportResponse,
): OriginalLayoutViewModel => {
  const { summary, brands, campaigns, daily_metrics, report_type } = data;
  const totalSales = parseNumber(summary.total_sales);
  const totalSpend = parseNumber(summary.total_spend);
  const averageRoas = parseNumber(summary.average_roas);
  const avgCtrRatio = parseOptionalNumber(summary.average_ctr);
  const avgCtrPercentage =
    avgCtrRatio !== null && Number.isFinite(avgCtrRatio)
      ? avgCtrRatio * 100
      : null;
  const ntbSalesValue = parseNumber(summary.total_ntb_sales);
  const ntbSalesRatio = parseOptionalNumber(summary.ntb_sales_ratio);
  const ntbSalesPercentage =
    ntbSalesRatio !== null && Number.isFinite(ntbSalesRatio)
      ? ntbSalesRatio * 100
      : null;
  const efficiency = totalSpend > 0 ? totalSales / totalSpend : 0;
  const avgDailySales = dailyAverage(totalSales, summary.total_days);
  const avgDailySpend = dailyAverage(totalSpend, summary.total_days);
  const brandNames = brands.map((brand) => brand.brand);
  const brandColorMap = generateBrandColors(brandNames);

  return {
    reportBadge:
      report_type === "search" ? "Sponsored Products" : "Display Ads",
    dateRangeLabel: `${summary.date_range_start} to ${summary.date_range_end}`,
    recordsProcessedLabel: `${data.records_processed} records`,
    daysLabel: `${summary.total_days} days`,
    overallMetrics: {
      totalSales: {
        title: "Total Sales",
        value: formatSales(summary.total_sales),
        subtitle: `${summary.total_units.toLocaleString()} units sold`,
        trend: "up",
      },
      avgDailySales: {
        title: "Avg Daily Sales",
        value: `$${formatMoney(avgDailySales, 0, 0)}`,
        subtitle: `${summary.total_days} days in range`,
        trend: avgDailySales >= 1000 ? "up" : "neutral",
      },
      totalSpend: {
        title: "Total Spend",
        value: formatSales(summary.total_spend),
        subtitle: `$${formatMoney(avgDailySpend, 0, 0)}/day average`,
        trend: "neutral",
      },
      roas: {
        title: "ROAS",
        value: `${averageRoas.toFixed(2)}x`,
        subtitle:
          efficiency >= 5
            ? "Excellent performance"
            : efficiency >= ROAS_THRESHOLDS.TARGET
              ? "Good performance"
              : "Needs improvement",
        trend: trendForRoas(averageRoas),
      },
      impressions: {
        title: "Impressions",
        value: formatSummaryImpressions(summary.total_impressions),
        subtitle: `${dailyAverageLabel(
          summary.total_impressions,
          summary.total_days,
        )} per day`,
        trend: "neutral",
      },
      totalClicks: {
        title: "Total Clicks",
        value: summary.total_clicks.toLocaleString(),
        subtitle: `${dailyAverageLabel(
          summary.total_clicks,
          summary.total_days,
        )} per day`,
        trend: "neutral",
      },
      clickRate: {
        title: "Click / Rate",
        value:
          avgCtrPercentage !== null
            ? `${avgCtrPercentage.toFixed(3)}%`
            : "N/A",
        subtitle:
          avgCtrPercentage !== null
            ? avgCtrPercentage >= CTR_THRESHOLDS.ABOVE_AVERAGE
              ? "Above average"
              : avgCtrPercentage >= CTR_THRESHOLDS.AVERAGE
                ? "Average"
                : "Below average"
            : "No CTR data",
        trend:
          avgCtrPercentage !== null
            ? avgCtrPercentage >= CTR_THRESHOLDS.ABOVE_AVERAGE
              ? "up"
              : avgCtrPercentage >= CTR_THRESHOLDS.AVERAGE
                ? "neutral"
                : "down"
            : "neutral",
      },
      ntbSalesShare: {
        title: "NTB Sales Share",
        value:
          ntbSalesPercentage !== null
            ? `${ntbSalesPercentage.toFixed(1)}%`
            : "N/A",
        subtitle:
          ntbSalesPercentage !== null
            ? `$${formatMoney(ntbSalesValue)} NTB sales`
            : "No NTB data",
        trend:
          ntbSalesPercentage !== null
            ? ntbSalesPercentage >= 30
              ? "up"
              : ntbSalesPercentage >= 15
                ? "neutral"
                : "down"
            : "neutral",
      },
    },
    brandCards: buildBrandCards(brands),
    brandSalesData: buildBrandSalesData(brands),
    salesDistributionSubtitle: `Total sales: $${formatMoney(
      summary.total_sales,
    )}`,
    roasChart: {
      labels: brandNames,
      values: brands.map((brand) => parseNumber(brand.roas)),
      colors: brandNames.map((brand) => brandColorMap[brand]),
    },
    roasTargetSubtitle: `Higher is better • Target: ${ROAS_THRESHOLDS.TARGET}x or above`,
    dailyTrendChart: buildDailyTrendChart(daily_metrics),
    dailyTrendSubtitle: `Sales vs Spend over ${summary.total_days} days`,
    campaignDetailsSubtitle: `${summary.campaign_count} active campaigns sorted by sales performance`,
    campaignRows: buildCampaignRows(campaigns),
  };
};
